import { type ReactNode, useRef } from 'react';
import { Dialog } from './Dialog';
import { Button } from '../primitives/Button';
import { Text } from '../primitives/Text';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  /** Body copy — a string renders as muted text; a node renders verbatim. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for destructive confirms (delete); `primary` otherwise. */
  tone?: 'danger' | 'primary';
  /** Disables the buttons + shows a spinner on confirm while an action is in flight. */
  loading?: boolean;
  confirmTestId?: string;
  cancelTestId?: string;
  'data-testid'?: string;
}

/**
 * The canonical confirm-before-acting dialog. Composes {@link Dialog}; focuses the
 * confirm button on open. Destructive confirms use the `danger` tone.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading,
  confirmTestId,
  cancelTestId,
  ...rest
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="xs"
      initialFocusRef={confirmRef}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading} data-testid={cancelTestId}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </Button>
        </>
      }
      {...rest}
    >
      {typeof description === 'string' ? (
        <Text tone="secondary" size="md">
          {description}
        </Text>
      ) : (
        description
      )}
    </Dialog>
  );
}
